// Rules and stretch library for the stretching routine builder. Pure, so the
// widget can build a routine during render. Sources are named per constant and
// cited on the tool page.

// ACSM position stand (Garber et al. 2011, Med Sci Sports Exerc 43:1334):
// "a series of flexibility exercises for each the major muscle-tendon groups
// (a total of 60 s per exercise) on ≥2 d·wk is recommended."
export const SECONDS_PER_STRETCH = 60

// Warneke et al. (2025, Journal of Sport and Health Science, Delphi consensus):
// for lasting range of motion, "2–3 sets daily, each held for 30–120 s per
// muscle". A 30 second hold done twice meets both statements.
export const HOLD_SECONDS = 30
export const SETS = SECONDS_PER_STRETCH / HOLD_SECONDS

// Time allowed to move into the next position or switch sides. Our estimate,
// used only to fit a routine into the minutes chosen.
export const TRANSITION_SECONDS = 10

export const MINUTE_OPTIONS = [5, 10, 15]

export type Area = 'neck' | 'chest' | 'back' | 'hips' | 'legs'
export type Position = 'standing' | 'seated' | 'floor'

export const AREAS: { value: Area; label: string }[] = [
    { value: 'neck', label: 'Neck and shoulders' },
    { value: 'chest', label: 'Chest and upper back' },
    { value: 'back', label: 'Lower back and sides' },
    { value: 'hips', label: 'Hips and glutes' },
    { value: 'legs', label: 'Legs and calves' }
]

export const POSITION_LABELS: Record<Position, string> = {
    standing: 'Standing',
    seated: 'On a chair',
    floor: 'On the floor'
}

export interface Stretch {
    id: string
    name: string
    area: Area
    position: Position
    // True when the stretch is done once for each side of the body.
    sided: boolean
    feel: string
    steps: string[]
}

// Written in our own words. The neck stretch, sideways bend and calf stretch
// follow the NHS "Flexibility exercises" routine; the rest are standard static
// stretches. Listed in the order the builder picks from within each area.
export const STRETCHES: Stretch[] = [
    {
        id: 'neck-side',
        name: 'Neck side stretch',
        area: 'neck',
        position: 'seated',
        sided: true,
        feel: 'Side of the neck',
        steps: [
            'Sit tall and look straight ahead with your shoulders relaxed.',
            'Hold the edge of the chair with one hand to keep that shoulder down.',
            'Slowly tilt your head toward the opposite shoulder until you feel a gentle stretch.'
        ]
    },
    {
        id: 'cross-body-shoulder',
        name: 'Cross-body shoulder stretch',
        area: 'neck',
        position: 'standing',
        sided: true,
        feel: 'Back of the shoulder',
        steps: [
            'Bring one arm straight across your chest at shoulder height.',
            'With the other hand, hold it just above the elbow.',
            'Gently draw the arm closer to your chest, keeping your shoulders down.'
        ]
    },
    {
        id: 'overhead-triceps',
        name: 'Overhead arm stretch',
        area: 'neck',
        position: 'standing',
        sided: true,
        feel: 'Back of the upper arm and shoulder',
        steps: [
            'Raise one arm and bend the elbow so your hand drops behind your head.',
            'With the other hand, hold the raised elbow.',
            'Gently ease the elbow back and toward your head, keeping your neck relaxed.'
        ]
    },
    {
        id: 'doorway-chest',
        name: 'Doorway chest stretch',
        area: 'chest',
        position: 'standing',
        sided: false,
        feel: 'Front of the chest and shoulders',
        steps: [
            'Stand in a doorway and place your forearms on the frame, elbows at shoulder height.',
            'Step one foot forward through the doorway.',
            'Lean forward slowly until you feel a stretch across your chest. Keep your back straight.'
        ]
    },
    {
        id: 'upper-back-hug',
        name: 'Upper back stretch',
        area: 'chest',
        position: 'seated',
        sided: false,
        feel: 'Between the shoulder blades',
        steps: [
            'Sit tall and wrap your arms around yourself as if giving yourself a hug.',
            'Reach your hands toward your shoulder blades.',
            'Round your upper back gently and let your head drop forward a little.'
        ]
    },
    {
        id: 'side-bend',
        name: 'Standing side bend',
        area: 'back',
        position: 'standing',
        sided: true,
        feel: 'Along the side of your body',
        steps: [
            'Stand tall with your feet hip-width apart and arms by your sides.',
            'Slide one hand down the outside of your leg as far as is comfortable.',
            'Keep your hips still and avoid leaning forward or back.'
        ]
    },
    {
        id: 'seated-twist',
        name: 'Seated twist',
        area: 'back',
        position: 'seated',
        sided: true,
        feel: 'Middle and lower back',
        steps: [
            'Sit tall near the front of a chair with your feet flat on the floor.',
            'Turn your upper body to one side and hold the back of the chair.',
            'Keep your hips facing forward and turn only as far as is comfortable.'
        ]
    },
    {
        id: 'knee-to-chest',
        name: 'Knee to chest',
        area: 'back',
        position: 'floor',
        sided: true,
        feel: 'Lower back and buttock',
        steps: [
            'Lie on your back with your knees bent and feet flat on the floor.',
            'Bring one knee toward your chest and hold it with both hands.',
            'Keep the other foot on the floor and your lower back relaxed.'
        ]
    },
    {
        id: 'childs-pose',
        name: 'Kneeling back stretch',
        area: 'back',
        position: 'floor',
        sided: false,
        feel: 'Length of the back',
        steps: [
            'Kneel on the floor, then sit back toward your heels.',
            'Walk your hands forward and lower your chest toward the floor.',
            'Rest your forehead down if comfortable and breathe slowly.'
        ]
    },
    {
        id: 'chair-figure-four',
        name: 'Seated figure-four stretch',
        area: 'hips',
        position: 'seated',
        sided: true,
        feel: 'Outer hip and buttock',
        steps: [
            'Sit tall on a chair and cross one ankle over the opposite knee.',
            'Let the crossed knee drop gently toward the floor.',
            'For more stretch, lean forward slightly from the hips with a straight back.'
        ]
    },
    {
        id: 'kneeling-hip-flexor',
        name: 'Kneeling hip flexor stretch',
        area: 'hips',
        position: 'floor',
        sided: true,
        feel: 'Front of the hip',
        steps: [
            'Kneel on one knee, with the other foot flat on the floor in front. Use a cushion under the knee.',
            'Keep your back upright and tuck your pelvis under slightly.',
            'Shift your weight forward gently until you feel a stretch at the front of the back hip.'
        ]
    },
    {
        id: 'butterfly',
        name: 'Seated butterfly stretch',
        area: 'hips',
        position: 'floor',
        sided: false,
        feel: 'Inner thighs',
        steps: [
            'Sit on the floor with the soles of your feet together and knees out to the sides.',
            'Hold your ankles and sit tall.',
            'Let your knees lower gently toward the floor without pushing them down.'
        ]
    },
    {
        id: 'calf-wall',
        name: 'Calf stretch at a wall',
        area: 'legs',
        position: 'standing',
        sided: true,
        feel: 'Back of the lower leg',
        steps: [
            'Rest your hands on a wall. Step one leg back, keeping it straight with the heel down.',
            'Bend the front knee and lean toward the wall.',
            'Keep both feet pointing forward and the back heel on the floor.'
        ]
    },
    {
        id: 'seated-hamstring',
        name: 'Seated hamstring stretch',
        area: 'legs',
        position: 'seated',
        sided: true,
        feel: 'Back of the thigh',
        steps: [
            'Sit near the front of a chair and stretch one leg out with the heel on the floor.',
            'Keep that leg straight but not locked, toes pointing up.',
            'Lean forward from the hips with a straight back until you feel a stretch.'
        ]
    },
    {
        id: 'standing-quad',
        name: 'Standing thigh stretch',
        area: 'legs',
        position: 'standing',
        sided: true,
        feel: 'Front of the thigh',
        steps: [
            'Stand next to a wall or chair and hold it for balance.',
            'Bend one knee and hold that foot or ankle behind you.',
            'Keep your knees together and stand tall. Use a towel around the ankle if you cannot reach.'
        ]
    }
]

export const stretchSeconds = (stretch: Stretch): number =>
    SETS * HOLD_SECONDS * (stretch.sided ? 2 : 1) + TRANSITION_SECONDS

export interface RoutineInput {
    areas: Area[]
    minutes: number
    noFloor: boolean
}

// Picks stretches area by area in turn until the time runs out, so every chosen
// area is covered before any area gets a second stretch.
export const buildRoutine = (input: RoutineInput): Stretch[] => {
    const budget = input.minutes * 60
    const pools = input.areas.map((area) =>
        STRETCHES.filter((stretch) => stretch.area === area && !(input.noFloor && stretch.position === 'floor'))
    )
    const routine: Stretch[] = []
    let used = 0
    let added = true

    while (added) {
        added = false
        for (const pool of pools) {
            const next = pool.shift()
            if (!next) continue
            const cost = stretchSeconds(next)
            if (used + cost > budget) continue
            routine.push(next)
            used += cost
            added = true
        }
    }

    return routine
}

export interface RoutineStep {
    key: string
    stretch: Stretch
    stretchIndex: number
    label: string
    seconds: number
}

// The routine as a sequence of timed holds for the follow-along timer.
export const routineSteps = (routine: Stretch[]): RoutineStep[] =>
    routine.flatMap((stretch, stretchIndex) => {
        const sides = stretch.sided ? ['Right side', 'Left side'] : ['']
        const steps: RoutineStep[] = []

        for (let set = 1; set <= SETS; set++) {
            for (const side of sides) {
                steps.push({
                    key: `${stretch.id}-${set}-${side || 'both'}`,
                    stretch,
                    stretchIndex,
                    label: `${side ? `${side}, ` : ''}hold ${set} of ${SETS}`,
                    seconds: HOLD_SECONDS
                })
            }
        }

        return steps
    })
